
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/lib/auth';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { trackSignUp, trackEmailVerified } from '@/lib/analytics';

interface User {
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name: string;
  initials: string;
  emailVerified: boolean;
  companyId?: string;
  companyTaxId?: string | null;
  companyBusinessName?: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  otpExpiresAt: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, registrationToken: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyEmail: () => void;
  verifyOtp: (code: string) => Promise<void>;
  resendOtp: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const isMock = import.meta.env.VITE_ENABLE_MOCK === 'true';

function getInitials(name: string, email?: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.slice(0, 2).toUpperCase();
  }
  return 'U';
}

function buildUser(data: {
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  emailVerified: boolean;
  companyId?: string;
  companyTaxId?: string | null;
  companyBusinessName?: string | null;
}): User {
  const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email.split('@')[0];
  return {
    id: data.id,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    name: fullName,
    initials: getInitials(fullName, data.email),
    emailVerified: data.emailVerified,
    companyId: data.companyId,
    companyTaxId: data.companyTaxId ?? null,
    companyBusinessName: data.companyBusinessName ?? null,
  };
}

const OTP_EXPIRES_KEY = 'flux_otp_expires_at';

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const saveOtpExpiresAt = (value: string | null) => {
    setOtpExpiresAt(value);
    if (value) {
      localStorage.setItem(OTP_EXPIRES_KEY, value);
    } else {
      localStorage.removeItem(OTP_EXPIRES_KEY);
    }
  };

  const handleEmailNotVerified = useCallback(() => {
    if (user) {
      const updated = { ...user, emailVerified: false };
      setUser(updated);
      localStorage.setItem('flux_user', JSON.stringify(updated));
    }
    navigate('/verify-email', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    authService.setSessionExpiredHandler(() => {
      setUser(null);
      localStorage.removeItem('flux_user');
    });

    authService.setEmailNotVerifiedHandler(handleEmailNotVerified);

    const init = async () => {
      try {
        if (isMock) {
          const stored = localStorage.getItem('flux_user');
          if (stored) {
            setUser(JSON.parse(stored));
          }
        } else {
          if (authService.isAuthenticated()) {
            const stored = localStorage.getItem('flux_user');
            if (stored) {
              setUser(JSON.parse(stored));
              setOtpExpiresAt(localStorage.getItem(OTP_EXPIRES_KEY));
              authService.startRefreshInterval();
            } else {
              try {
                const url = getEndpointUrl('auth', 'me');
                const userData = await httpClient.get<{
                  id: string;
                  email: string;
                  firstName?: string;
                  lastName?: string;
                  emailVerified: boolean;
                  companyId?: string;
                  companyTaxId?: string | null;
                  companyBusinessName?: string | null;
                  otpExpiresAt?: string;
                }>(url);
                const u = buildUser(userData);
                setUser(u);
                localStorage.setItem('flux_user', JSON.stringify(u));
                if (userData.otpExpiresAt) {
                  saveOtpExpiresAt(userData.otpExpiresAt);
                }
                authService.startRefreshInterval();
              } catch {
                authService.clearTokens();
              }
            }
          }
        }
      } catch {
        // ignore
      }
      setIsLoading(false);
    };

    init();

    return () => {
      authService.setSessionExpiredHandler(null);
      authService.setEmailNotVerifiedHandler(null);
    };
  }, []);

  const login = async (email: string, password: string) => {
    if (isMock) {
      await new Promise((r) => setTimeout(r, 600));
      const stored = localStorage.getItem('flux_users');
      const users: Record<string, { password: string; emailVerified: boolean }> = stored
        ? JSON.parse(stored)
        : {};

      if (!users[email] || users[email].password !== password) {
        throw new Error('Correo o contraseña incorrectos');
      }

      const u = buildUser({ email, emailVerified: users[email].emailVerified });
      setUser(u);
      localStorage.setItem('flux_user', JSON.stringify(u));
      return;
    }

    const url = getEndpointUrl('auth', 'login');
    try {
      const response = await httpClient.post(url, { email, password });
      const data = await response.json();
      authService.setTokens(data.accessToken, data.refreshToken);
      const u = buildUser(data.user);
      setUser(u);
      localStorage.setItem('flux_user', JSON.stringify(u));
      saveOtpExpiresAt(data.otpExpiresAt ?? null);
    } catch (err: unknown) {
      const status = (err as any)?.status;
      if (status === 401) {
        throw new Error('Correo o contraseña incorrecta');
      }
      // 500 ya no se mapea a "credenciales incorrectas" — engaña al usuario y
      // dispara reset-password sin necesidad. Re-lanzamos como error genérico
      // para que la UI lo distinga de credenciales malas.
      if (status === 500) {
        throw new Error('Servicio no disponible. Intenta nuevamente en unos minutos.');
      }
      throw err;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, registrationToken: string) => {
    if (isMock) {
      await new Promise((r) => setTimeout(r, 600));
      const stored = localStorage.getItem('flux_users');
      const users: Record<string, { password: string; emailVerified: boolean; firstName: string; lastName: string }> = stored
        ? JSON.parse(stored)
        : {};

      if (users[email]) {
        throw new Error('Este correo ya está registrado');
      }

      users[email] = { password, emailVerified: false, firstName, lastName };
      localStorage.setItem('flux_users', JSON.stringify(users));

      const u = buildUser({ email, firstName, lastName, emailVerified: false });
      setUser(u);
      localStorage.setItem('flux_user', JSON.stringify(u));
      return;
    }

    const url = getEndpointUrl('auth', 'register');
    const response = await httpClient.post(url, { email, password, firstName, lastName, registrationToken });
    const data = await response.json();
    authService.setTokens(data.accessToken, data.refreshToken);
    const u = buildUser(data.user);
    setUser(u);
    localStorage.setItem('flux_user', JSON.stringify(u));
    saveOtpExpiresAt(data.otpExpiresAt ?? null);
    trackSignUp();
  };

  const logout = async () => {
    if (!isMock) {
      try {
        const url = getEndpointUrl('auth', 'logout');
        const refreshToken = authService.getRefreshToken();
        await httpClient.post(url, { refreshToken });
      } catch {
        // ignore logout errors
      }
      authService.clearTokens();
    }
    // Limpia el caché HTTP para que el siguiente usuario que se loguee en el
    // mismo navegador no vea respuestas cacheadas del usuario anterior.
    httpClient.clearCache();
    setUser(null);
    localStorage.removeItem('flux_user');
    saveOtpExpiresAt(null);
  };

  const verifyEmail = () => {
    if (!user) return;
    if (isMock) {
      const stored = localStorage.getItem('flux_users');
      const users: Record<string, { password: string; emailVerified: boolean }> = stored
        ? JSON.parse(stored)
        : {};
      if (users[user.email]) {
        users[user.email].emailVerified = true;
        localStorage.setItem('flux_users', JSON.stringify(users));
      }
    }
    const updated = { ...user, emailVerified: true };
    setUser(updated);
    localStorage.setItem('flux_user', JSON.stringify(updated));
    saveOtpExpiresAt(null);
  };

  const verifyOtp = async (code: string) => {
    if (isMock) {
      await new Promise((r) => setTimeout(r, 400));
      if (code !== '123456') {
        throw new Error('Código incorrecto. En modo mock el código es 123456');
      }
      verifyEmail();
      return;
    }

    const url = getEndpointUrl('auth', 'verify-otp');
    const refreshToken = authService.getRefreshToken();
    const response = await httpClient.post(url, { code, refreshToken });
    const data = await response.json();
    authService.setTokens(data.accessToken, data.refreshToken);
    verifyEmail();
    trackEmailVerified();
  };

  const resendOtp = async () => {
    if (isMock) {
      await new Promise((r) => setTimeout(r, 400));
      return;
    }

    const url = getEndpointUrl('auth', 'resend-otp');
    const response = await httpClient.post(url, {});
    const data = await response.json();
    if (data.otpExpiresAt) {
      saveOtpExpiresAt(data.otpExpiresAt);
    }
  };

  const forgotPassword = async (email: string) => {
    if (isMock) {
      await new Promise((r) => setTimeout(r, 600));
      return;
    }

    const url = getEndpointUrl('auth', 'forgot-password');
    await httpClient.post(url, { email });
  };

  const resetPassword = async (token: string, password: string) => {
    if (isMock) {
      await new Promise((r) => setTimeout(r, 600));
      return;
    }

    const url = getEndpointUrl('auth', 'reset-password');
    await httpClient.post(url, { token, newPassword: password });
  };

  // Re-fetch del usuario actual desde /v1/auth/me. Necesario después de operaciones que
  // mutan datos derivados de la empresa (p. ej. guardar credenciales SII actualiza
  // Company.taxId, y el RUT del titular bancario se prellena desde aquí).
  const refreshUser = useCallback(async () => {
    if (isMock) return;
    if (!authService.isAuthenticated()) return;

    const url = getEndpointUrl('auth', 'me');
    const userData = await httpClient.get<{
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      emailVerified: boolean;
      companyId?: string;
      companyTaxId?: string | null;
      companyBusinessName?: string | null;
    }>(url);
    const u = buildUser(userData);
    setUser(u);
    localStorage.setItem('flux_user', JSON.stringify(u));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        otpExpiresAt,
        login,
        register,
        logout,
        verifyEmail,
        verifyOtp,
        resendOtp,
        forgotPassword,
        resetPassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
